/**
 * BatchRunsList - List of batch generation jobs
 */

import { useQuery } from '@tanstack/react-query';
import { Package, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { listBatchRuns } from '@/lib/api/batch';
import { Button, EmptyState } from '@/components/v2/ui';
import { BatchRunCard } from './BatchRunCard';

interface BatchRunsListProps {
  selectedRunId?: string;
  onSelectRun?: (runId: string) => void;
}

export function BatchRunsList({ selectedRunId, onSelectRun }: BatchRunsListProps) {
  const { t } = useTranslation();
  const { data: runs, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['batch', 'runs'],
    queryFn: () => listBatchRuns(undefined, 50),
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="h-8 w-8 text-orange-500 animate-spin" />
          <p className="text-sm text-neutral-500">Lade Batch-Jobs...</p>
        </div>
      </div>
    );
  }

  if (!runs || runs.length === 0) {
    return (
      <EmptyState
        icon={<Package className="h-12 w-12 text-orange-500" />}
        title="Keine Batch-Jobs"
        description="Erstelle deinen ersten Batch-Job mit dem Formular rechts."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
          {runs.length} {runs.length === 1 ? 'Job' : 'Jobs'}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          iconLeft={<RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />}
        >
          {t('common.refresh')}
        </Button>
      </div>

      <div className="space-y-3">
        {runs.map((run) => (
          <BatchRunCard
            key={run.id}
            run={run}
            isSelected={selectedRunId === run.id}
            onSelect={onSelectRun}
          />
        ))}
      </div>
    </div>
  );
}
